import { useMemo } from 'react'
import { usePricing } from '../contexts/PricingContext'
import { getBuildPaymentPlan } from '../utils/buildPaymentPlan'
import {
  FinancialModelData,
  buildFinancialModel,
  inputsFromDeck,
} from '../utils/financialModel'

/**
 * Business assumptions (ticket, margin, rep output, retention, ad spend) come
 * from the ROI calculator once it's been changed, otherwise from the deck's ad
 * spend and cost-per-hire range. Pricing choices (recruiter, leader search,
 * pricing model) always follow the deck, so the model matches the two-pagers.
 */
export function useFinancialModel(): FinancialModelData {
  const {
    calculatorInputs,
    calculatorScenario,
    adSpend,
    cpaLow,
    cpaHigh,
    hasRecruiter,
    hasLeader,
    pricingModel,
    buildFee,
    leaderBuildFee,
    splitBuildFee,
    isPromo,
    hasImplementationFee,
  } = usePricing()

  return useMemo(() => {
    const leaderMonths = hasLeader ? 2 : 0
    // The proposal's implementation choice (promo price or 3-way split)
    // flows into the model so page 4 bills what page 2 promises.
    const implementation = buildFee + (hasLeader ? leaderBuildFee : 0)
    const implementationSchedule = (() => {
      if (!hasImplementationFee || (!isPromo && !splitBuildFee)) return null
      const fees = Array(40).fill(0) as number[]
      if (splitBuildFee) {
        const plan = getBuildPaymentPlan(implementation)
        fees[0] = plan.down
        // Payments 2 and 3 land in campaign Months 2 and 3.
        fees[leaderMonths + 2] = plan.monthly
        fees[leaderMonths + 3] = plan.monthly
      } else {
        fees[0] = implementation
      }
      return fees
    })()
    const deckInputs = inputsFromDeck({ adSpend, cpaLow, cpaHigh })
    // The calculator pushes its ad spend and cost per hire to the deck, so the
    // deck holds the latest values. If they've since been changed elsewhere
    // (e.g. the proposal's side panel), those win over the calculator's.
    const calcSpend = calculatorInputs
      ? calculatorInputs.adSpend * calculatorInputs.campaigns
      : 0
    const calcLow = calculatorInputs?.cphLow ?? (calculatorInputs ? calculatorInputs.cph - 150 : 0)
    const calcHigh = calculatorInputs?.cphHigh ?? (calculatorInputs ? calculatorInputs.cph + 150 : 0)
    const spendMoved = calculatorInputs && Math.round(calcSpend) !== Math.round(adSpend)
    const cphMoved = calculatorInputs && (calcLow !== cpaLow || calcHigh !== cpaHigh)
    const base = !calculatorInputs
      ? deckInputs
      : {
          ...calculatorInputs,
          ...(spendMoved
            ? {
                campaigns: deckInputs.campaigns,
                monthlyCampaigns: deckInputs.monthlyCampaigns,
                adSpend: deckInputs.adSpend,
                monthlyAdSpend: deckInputs.monthlyAdSpend,
              }
            : {}),
          ...(cphMoved
            ? { cphLow: cpaLow, cphHigh: cpaHigh, cph: deckInputs.cph }
            : {}),
        }
    const inputs = {
      ...base,
      addRecruiter: hasRecruiter,
      pricingModel:
        pricingModel === 'flagship' ? ('legacy' as const) : ('monthly' as const),
      noImplementationFee: !hasImplementationFee,
      // The search is modeled conservatively at its full 8 weeks: two months
      // of search fees and ads before campaign Month 1.
      leaderCampaignMonths: leaderMonths,
      ...(implementationSchedule
        ? { monthlyBuildFees: implementationSchedule }
        : {}),
    }
    return buildFinancialModel(inputs, calculatorScenario)
  }, [
    calculatorInputs,
    calculatorScenario,
    adSpend,
    cpaLow,
    cpaHigh,
    hasRecruiter,
    hasLeader,
    pricingModel,
    buildFee,
    leaderBuildFee,
    splitBuildFee,
    isPromo,
    hasImplementationFee,
  ])
}
